/**
 * Loan Compare arithmetic (MOD-08), kept apart from the page so it can be
 * tested: inputs in $MM whatever units the record uses, and metrics that are
 * null — not Infinity — when an input is missing.
 */
import { amountUnitOf, propertyValueOf, recordedNoiOf, toMillions } from '@/pages/liveModuleData'
import type { Loan } from '@/types'

export interface ScenarioData {
  label: string
  loanAmount: number
  ltv: number
  product: string
  rate: string
  spreadBps: number
  term: string
  ioPeriod: string
  amortization: string
  prepay: string
  rateFloor: number
  extension: string
  recourse: string
}

/** Null where an input is missing: no loan, no NOI, or no SOFR print. */
export interface ComputedMetrics {
  dscr: number | null
  debtYield: number | null
  annualDS: number | null
  cashOnCash: number | null
  stressDownside: number | null
  stressSevere: number | null
  guideline: 'in-box' | 'stretch' | 'out-of-box' | 'unknown'
}

/**
 * The deal's inputs in $MM, whichever units the record uses, and where the
 * value came from. Live loans are in DOLLARS; appending "M" to them printed
 * The Marlowe's value as "$37692307.7M" (MOD-08).
 */
export interface ComparisonInputs {
  propertyValue: number
  noi: number
  /** 'derived': a value the platform computed (a component valuation). */
  valueSource: 'record' | 'derived' | 'none'
}

export function comparisonInputs(deal: Loan | undefined): ComparisonInputs {
  if (!deal) return { propertyValue: 0, noi: 0, valueSource: 'none' }
  const unit = amountUnitOf(deal)
  const noiDollarsOrM = recordedNoiOf(deal)
  const noi = noiDollarsOrM != null ? toMillions(noiDollarsOrM, unit) : 0
  const reading = propertyValueOf(deal)
  if (reading != null) {
    return {
      propertyValue: toMillions(reading.value, unit),
      noi,
      valueSource: reading.state === 'derived' ? 'derived' : 'record',
    }
  }
  // R3: a stated LTV is its own sourced figure and never back-solves value.
  // Loan ÷ LTV printed Sun Valley's value as "$13.1M" and sized scenarios off it.
  return { propertyValue: 0, noi, valueSource: 'none' }
}

export function computeMetrics(scenario: ScenarioData, noi: number, propertyValue: number, sofrRate: number | null): ComputedMetrics {
  const loan = scenario.loanAmount
  // With no loan there is nothing to cover; with no NOI or no SOFR print the
  // coverage is unknown. Dividing anyway printed "Infinityx" DSCR and a PASS.
  const annualDS = loan > 0 && sofrRate != null ? loan * (sofrRate + scenario.spreadBps / 10000) : null
  const hasNoi = noi > 0
  const dscr = annualDS && hasNoi ? noi / annualDS : null
  const debtYield = loan > 0 && hasNoi ? noi / loan : null
  const equity = propertyValue - loan
  const cashOnCash = annualDS && hasNoi && equity > 0 ? ((noi - annualDS) / equity) * 100 : null
  const stressDownside = annualDS && hasNoi ? (noi * 0.85) / annualDS : null
  const stressSevere = annualDS && hasNoi ? (noi * 0.70) / annualDS : null
  const guideline: ComputedMetrics['guideline'] =
    dscr == null || !(scenario.ltv > 0)
      ? 'unknown'
      : scenario.ltv <= 0.72 && dscr >= 1.25 ? 'in-box' : scenario.ltv <= 0.80 && dscr >= 1.10 ? 'stretch' : 'out-of-box'

  return { dscr, debtYield, annualDS, cashOnCash, stressDownside, stressSevere, guideline }
}
